import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { cambiarEstadoDeCuenta, consultarCuentas, CLASES } from '../api/cuentasApi.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { PestanasDeCuentas } from '../componentes/PestanasDeCuentas.jsx';
import { agruparPorMoneda, importe, iniciales, NOMBRE_DE_MONEDA, vigencia } from '../formato.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

/**
 * Lista de cuentas — pestaña "Cuentas" (RF-041, D-27, D-29).
 *
 * DECISIONES QUE ESTA PANTALLA HACE CUMPLIR, y ninguna se decide aquí:
 *   * Bloques SEPARADOS POR MONEDA, con su total, sin total combinado y sin
 *     conversión (RN-003, RN-033). La deuda de tarjetas NO se resta del total de
 *     saldos: va en su propio bloque (S-21).
 *   * ORDEN ALFABÉTICO resuelto por el SERVIDOR. Esta pantalla NO reordena y no
 *     hay pantalla de reordenar en este incremento (D-29, RN-071).
 *   * Las cuentas DESACTIVADAS están OCULTAS tras un control de esta misma
 *     pantalla; al encenderlo aparecen al final de su bloque de moneda, en gris y
 *     con Reactivar (D-27, RN-072a-b).
 *   * El estado del control NO se recuerda entre visitas: al volver a entrar, la
 *     lista arranca otra vez solo con activas (RN-072c). Por eso el estado vive en
 *     el componente y no en almacenamiento del navegador.
 *   * Los totales NO cambian al ver las desactivadas: los calcula el servidor sobre
 *     las activas (RN-072d).
 */
export default function ListaDeCuentas() {
  const [verDesactivadas, setVerDesactivadas] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [errorDeAccion, setErrorDeAccion] = useState(null);

  /*
   * CUATRO ESTADOS EXCLUYENTES (RF-087, D-83). El estado inicial es CARGANDO, no
   * VACIO: la pantalla ya no afirma "todavía no tienes cuentas" antes de tener la
   * respuesta, que era justo el defecto levantado (RN-141).
   *
   * Cambiar el control de desactivadas vuelve a CARGANDO porque `consultar`
   * cambia de identidad (RN-142b), y la recarga conserva la lista visible con la
   * barra de actualización (RN-142a).
   */
  /*
   * ===========================================================================
   * CORREGIDO · FIX del 2026-10-08 · RF-220, RN-625, D-322 · LA CIFRA DE DEUDA
   * ===========================================================================
   * En producción esta pantalla mostraba «Deuda de tarjetas en MXN: $0.00»
   * teniendo planes de MSI vigentes y con el servidor respondiendo
   * `totalDeudas` = 10 088.20 en la misma consulta. La causa real NO fue que la
   * pantalla perdiera la cifra: fue que PREGUNTABA MAL.
   *
   * Pedía `clase=NO_CREDITO`, y el servidor arma `totalesPorMoneda` agrupando
   * las cuentas que la consulta trajo (ArmarTotales): sin tarjetas en el
   * conjunto, el renglón de deuda sale honestamente en cero, porque la deuda de
   * las cuentas que no son de crédito ES cero. La pantalla presentaba ese cero
   * —que responde a otra pregunta— como si fuera la deuda del hogar. Mismo
   * nombre, dos valores: defecto de RN-600, y el «$0.00» que contradecía
   * RN-069e con planes abiertos.
   *
   * AHORA SE PIDE `clase=TODAS` y la cifra de deuda es `totalDeudas` TAL CUAL
   * del contrato, del mismo instante y de la misma respuesta que el saldo
   * (RN-625): una sola cifra de deuda en todo el producto, la misma que muestra
   * la pestaña de Tarjetas.
   *
   * Las TARJETAS no se listan en esta pestaña —eso es la pestaña Tarjetas— así
   * que se dejan fuera al DIBUJAR, con `esCredito`, que es campo contratado
   * (RF-044). Es filtro de PRESENTACIÓN sobre un dato que el servidor ya
   * resolvió, igual que el de las categorías que avisan del Tablero: no es
   * aritmética nueva, no reordena nada y no reabre ningún contrato.
   */
  const consultar = useCallback(
    () => consultarCuentas({ soloActivas: !verDesactivadas, clase: CLASES.todas }),
    [verDesactivadas],
  );

  /* «Sin cuentas» es sin cuentas QUE ESTA PESTAÑA MUESTRA: un hogar que solo
     tuviera tarjetas seguiría viendo aquí su invitación a crear la primera
     cuenta, no una pantalla en blanco. */
  const listado = useConsultaDeListado(
    consultar,
    (respuesta) => (respuesta?.cuentas ?? []).filter(esDeEstaPestana).length === 0,
  );
  const { datos, estado } = listado;
  const cargar = listado.recargar;

  async function reactivar(cuenta) {
    setErrorDeAccion(null); setAviso(null);
    try {
      // Reactivar es INMEDIATO: no pide confirmación y no consulta nada (RN-042).
      await guardarConConexion(() => cambiarEstadoDeCuenta(cuenta.idCuenta, { activo: true, version: cuenta.version }));
      setAviso(`Cuenta “${cuenta.nombre}” reactivada. Vuelve a su lugar alfabético.`);
      await cargar();
    } catch (fallo) {
      setErrorDeAccion(fallo);
    }
  }

  const cuentas = (datos?.cuentas ?? []).filter(esDeEstaPestana);
  const bloques = agruparPorMoneda(cuentas);
  const totalDe = (idMoneda) => (datos?.totalesPorMoneda ?? []).find((t) => t.idMoneda === idMoneda);

  return (
    <Shell titulo="Cuentas y tarjetas" subtitulo="Saldos calculados, nunca almacenados">
      <PestanasDeCuentas activa="cuentas" />

      <AvisoDeError error={errorDeAccion} />
      {aviso && <div className="aviso ok">✔ {aviso}</div>}
      {(datos?.advertencias ?? []).map((texto) => (
        <div key={texto} className="aviso w">⚠ {texto}</div>
      ))}

      <ListadoActualizando visible={listado.actualizando} />

      {/* RN-142c: mientras carga NO se pintan ni los totales por divisa en cero ni
          el bloque de "aún no tienes cuentas". */}
      {estado === ESTADO_DE_LISTADO.CARGANDO && <ListadoCargando renglones={4} texto="Cargando tus cuentas…" />}

      {estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={cargar} />
      )}

      {estado === ESTADO_DE_LISTADO.VACIO ? (
        <>
          <div className="card" style={{ margin: 12 }}>
            <div style={{ textAlign: 'center', padding: '22px 16px' }}>
              <p><b>Todavía no tienes cuentas.</b></p>
              <p>
                Registra tu primera cuenta con su <b>saldo inicial</b> —lo que tenía hoy— para que
                después los movimientos tengan de dónde salir. Puedes registrar
                <b> efectivo, débito, ahorro o una tarjeta de crédito</b>.
              </p>
              <Link className="btn" to="/cuentas/nueva">Registrar mi primera cuenta</Link>
            </div>
          </div>
          <div className="aviso w">
            <b>Si el hogar no tiene ninguna cuenta activa</b>, al registrar un movimiento no aparece un
            selector de cuentas vacío: aparece este mismo aviso, con acceso directo a crear cuenta.
          </div>
        </>
      ) : estado === ESTADO_DE_LISTADO.CON_DATOS ? (
        <>
          <div className="ayuda" style={{ margin: '10px 12px 0' }}>
            <b>ORDEN</b> · alfabético por nombre dentro de cada bloque de moneda. Lo resuelve el
            servidor y no se reordena a mano.
          </div>

          {bloques.map((bloque) => {
            const total = totalDe(bloque.idMoneda);
            const activas = bloque.cuentas.filter((c) => c.activo);
            const desactivadas = bloque.cuentas.filter((c) => !c.activo);

            return (
              <div key={bloque.idMoneda} style={{ margin: '12px 12px 0' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>
                  {(NOMBRE_DE_MONEDA[bloque.codigoISO] ?? bloque.codigoISO).toUpperCase()} · {bloque.codigoISO}
                </h4>

                {/* RN-624 · UN TOTAL QUE NO LLEGÓ NO ES CERO. El servidor deja
                    `totalesPorMoneda` vacío cuando no pudo calcular los
                    acumulados y lo avisa arriba, en `advertencias`: entonces
                    aquí no se escribe un importe inventado. `$0.00` solo puede
                    significar que el servidor respondió exactamente 0.00. */}
                <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span>Saldo total en {bloque.codigoISO}</span>
                  <b style={{ fontSize: 19 }} data-prueba="saldo-total-de-la-moneda" data-moneda={bloque.codigoISO}>
                    {total ? importe(total.totalSaldos, bloque.codigoISO) : 'No disponible'}
                  </b>
                </div>

                {/* RF-280 · DEUDA DE TARJETAS, Y SOLO DE TARJETAS (D-474). La
                    pantalla no calcula: presenta `totalDeudas` tal cual del
                    contrato (RN-625), y desde la rev. 12 el servidor lo arma
                    con la lista explícita de tipo CRÉDITO (RN-797). Por eso
                    esta cifra y la de la pestaña Tarjetas son idénticas centavo
                    a centavo (RN-799, CA-23-01, CA-23-03). */}
                <div className="ayuda" style={{ margin: '-4px 2px 8px' }}>
                  Deuda de tarjetas en {bloque.codigoISO}:{' '}
                  <b data-prueba="deuda-de-tarjetas-de-la-moneda" data-moneda={bloque.codigoISO}>
                    {total ? importe(total.totalDeudas, bloque.codigoISO) : 'No disponible'}
                  </b> ·
                  no se resta de este total · <Link to="/cuentas/tarjetas">ver tarjetas</Link>
                </div>

                {/* RF-281 · RENGLÓN PROPIO DE LOS PRÉSTAMOS (D-474, D-475).
                    SE PINTA SIEMPRE, también cuando no hay ningún préstamo: en
                    ese caso el servidor responde "0.00" y eso es lo que se
                    muestra (RN-800, CA-23-05). Ocultarlo crearía un tercer
                    estado mudo en el que no se podría distinguir «no debo
                    préstamos» de «la cifra no se pudo leer», que es justo lo
                    que D-106 y RN-624 prohíben.
                    Igual que el de tarjetas: `totalPrestamos` tal cual, sin
                    petición nueva y sin aritmética en pantalla (RNF-146), por
                    moneda y sin conversión (RN-798), y no se resta del saldo. */}
                <div className="ayuda" style={{ margin: '-4px 2px 8px' }} data-prueba="renglon-prestamos">
                  Deuda de préstamos en {bloque.codigoISO}:{' '}
                  <b data-prueba="deuda-de-prestamos-de-la-moneda" data-moneda={bloque.codigoISO}>
                    {total && total.totalPrestamos != null
                      ? importe(total.totalPrestamos, bloque.codigoISO)
                      : 'No disponible'}
                  </b> ·
                  tampoco se resta de este total
                </div>

                <div className="card lista">
                  {activas.map((cuenta) => <FilaDeCuenta key={cuenta.idCuenta} cuenta={cuenta} />)}

                  {verDesactivadas && desactivadas.length > 0 && (
                    <>
                      <div className="ayuda" style={{ paddingTop: 8, borderTop: '1px dashed var(--bor)' }}>
                        Desactivadas · al final de este bloque
                      </div>
                      {desactivadas.map((cuenta) => (
                        <FilaDeCuenta key={cuenta.idCuenta} cuenta={cuenta} alReactivar={() => reactivar(cuenta)} />
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <button
            className="btn sec ancho"
            type="button"
            aria-pressed={verDesactivadas}
            onClick={() => setVerDesactivadas(!verDesactivadas)}
          >
            {verDesactivadas ? 'Ocultar' : 'Ver'} cuentas desactivadas ({datos?.cantidadDesactivadas ?? 0})
          </button>

          {verDesactivadas && (
            <div className="aviso i">
              <b>Estás viendo también las desactivadas.</b> Aparecen en gris, al final de su bloque de
              moneda, con la acción Reactivar, que es inmediata y no pide confirmación. <b>Los totales de arriba no cambiaron</b>: se calculan siempre sobre las
              cuentas activas. Y no esconden dinero: una cuenta desactivada tiene
              forzosamente saldo cero, porque de otro modo no se habría podido desactivar.
            </div>
          )}

          <div className="aviso i">
            Los saldos van en <b>bloques separados por moneda y nunca se suman entre sí</b>. Ves las cuentas de las dos personas del hogar; <b>solo su propietario
            puede editarlas, renovarlas o desactivarlas</b>. Las cuentas de
            <b> Ahorro</b> suman en este mismo total, junto con Efectivo y Débito.
          </div>
        </>
      ) : null}

      <div style={{ margin: '10px 12px' }}>
        <Link className="btn ancho" to="/cuentas/nueva">Nueva cuenta</Link>
        {/* INCREMENTO 20/21 · El préstamo tiene su propia alta (P-20-01) porque pide
            cosas que una cuenta normal no tiene —monto original, pago mensual,
            plazo— y porque nace con su plan de pagos en el mismo acto (RF-264,
            RF-265). El alta genérica sigue intacta para los cuatro tipos de
            siempre. */}
        <Link className="btn sec ancho" to="/cuentas/prestamos/nuevo" data-prueba="alta-de-prestamo">
          Nuevo préstamo
        </Link>
      </div>
    </Shell>
  );
}

/**
 * QUÉ CUENTAS MUESTRA ESTA PESTAÑA, POR LISTA EXPLÍCITA DE TIPO (RN-752, incremento
 * 20/21): todo lo que NO es tarjeta de crédito —efectivo, débito, ahorro y PRÉSTAMO—.
 *
 * ANTES SE PREGUNTABA POR `esCredito` y eso dejó de ser correcto el día que nació el
 * tipo Préstamo: un préstamo también lleva esa bandera —dice «es pasivo», no «es
 * tarjeta»— y con la regla vieja habría desaparecido de las dos pestañas, porque la
 * de Tarjetas pide su universo por tipo CRÉDITO. Para los cuatro tipos de siempre el
 * resultado es IDÉNTICO.
 */
function esDeEstaPestana(cuenta) {
  return cuenta.claveTipoCuenta !== 'CREDITO';
}

function FilaDeCuenta({ cuenta, alReactivar }) {
  const detalle = (
    <>
      <div className="punto" aria-hidden="true">{iniciales(cuenta.nombre)}</div>
      <div className="crece">
        <b>{cuenta.nombre}</b>
        <small>
          {cuenta.nombreTipoCuenta} · {cuenta.esPropia ? 'Tuya' : `de ${cuenta.nombrePropietario}`}
          {cuenta.cuatroUltimosDigitos && ` · ••${cuenta.cuatroUltimosDigitos}`}
          {vigencia(cuenta.vigenciaMes, cuenta.vigenciaAnio) && ` · vence ${vigencia(cuenta.vigenciaMes, cuenta.vigenciaAnio)}`}
          {!cuenta.activo && ' · desactivada'}
          {cuenta.activo && cuenta.puedeDesactivarse && ' · en cero · se puede desactivar'}
        </small>
      </div>
      {/* En un PRÉSTAMO la cifra del renglón es su DEUDA, no un saldo: es un pasivo
          y su saldo viaja nulo (D-24, D-434). La deriva el servidor con la pieza
          única (D-52): aquí no se calcula nada. */}
      <span className="imp">
        {cuenta.claveTipoCuenta === 'PRESTAMO'
          ? importe(cuenta.deuda ?? cuenta.saldoInicial, cuenta.codigoISO)
          : importe(cuenta.saldo ?? cuenta.saldoInicial, cuenta.codigoISO)}
      </span>
    </>
  );

  if (!cuenta.activo) {
    return (
      <div className="fila" style={{ opacity: 0.62 }}>
        {detalle}
        <button className="btn sec" type="button" onClick={alReactivar}>Reactivar</button>
      </div>
    );
  }

  /*
   * UN PRÉSTAMO ABRE SU PROPIO DETALLE (P-20-02) y no el de una cuenta normal, y es
   * deliberado: ahí viven su plan de pagos, su conteo, su calendario y el historial
   * de pagos con su reparto, que una cuenta no tiene. El detalle genérico sigue
   * accesible desde ahí con «Ver la cuenta», para corregir nombre, notas o el saldo
   * inicial con su motivo.
   */
  const destino = cuenta.claveTipoCuenta === 'PRESTAMO'
    ? `/cuentas/prestamos/${cuenta.idCuenta}`
    : `/cuentas/${cuenta.idCuenta}`;

  return (
    <Link className="fila" to={destino}>{detalle}</Link>
  );
}
